import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from 'src/app/services/notification.service';
import { catchError, distinctUntilChanged, finalize, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { helper } from 'src/environments/helper';
import { ProgramService } from '../program.service';
import { ProgramUpdate } from '../program.interface';
import { TipuriProblemeService } from '../../tipuri-probleme/tipuri-probleme.service';
import { Generic } from 'src/app/interfaces/generic.interface';

@Component({
    selector: 'app-edit-program',
    templateUrl: './edit-program.component.html',
    styleUrls: ['./edit-program.component.scss']
})
export class EditProgramComponent {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    token!: string;
    isLoading = false;

    defaultParameter: { name: string; id: string } = {
        name: '-- Alegeți --',
        id: '-1',
    };

    module: Generic[] = this.data.module;
    locatii: Generic[] = this.data.locatii;
    specializari: Generic[] = this.data.specializari;
    operatori: any[] = this.data.operatori;
    tipuriProbleme!: Generic[];

    private addDestroy$ = new Subject<void>();

    // Filtered fields for search
    filteredModule: any[] = this.data.module;
    moduleControl = new FormControl();

    filteredLocatii: any[] = this.data.locatii;
    locatiiControl = new FormControl();

    filteredSpecializari: any[] = this.data.specializari;
    specializariControl = new FormControl();

    filteredOperatori: any[] = this.data.operatori;
    operatoriControl = new FormControl();

    filteredTipuriProbleme: any[] = [];
    tipuriProblemeControl = new FormControl();

    form: FormGroup = new FormGroup({
        modul_id: new FormControl(this.data.data.modul_id, Validators.required),
        locatie_id: new FormControl(this.data.data.locatie_id, Validators.required),
        specializare_id: new FormControl(this.data.data.specializare_id, Validators.required),
        specialist_id: new FormControl(this.data.data.specialist_id, Validators.required),
        nume_specialist: new FormControl(this.data.data.nume_specialist, Validators.required),
        tip_problema_id: new FormControl(this.data.data.tip_problema_id, Validators.required),
    });

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialogRef: MatDialogRef<EditProgramComponent>,
        private programService: ProgramService,
        private tipuriProblemeService: TipuriProblemeService,
        private notificationService: NotificationService,
    ) { }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    ngOnInit(): void {
        this.loadTipuriProbleme();
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.addDestroy$.next();
        this.addDestroy$.complete();
    }

    renderFieldsSearch() {
        this.moduleControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredModule = this.module;
                    return of(this.module);
                }
                return this.filterModule(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredModule = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.locatiiControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredLocatii = this.locatii;
                    return of(this.locatii);
                }
                return this.filterLocatii(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredLocatii = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.specializariControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredSpecializari = this.specializari;
                    return of(this.specializari);
                }
                return this.filterSpecializari(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredSpecializari = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.operatoriControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredOperatori = this.operatori;
                    return of(this.operatori);
                }
                return this.filterOperatori(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredOperatori = data;
            }),
            catchError(() => of([]))
        ).subscribe();

        this.tipuriProblemeControl.valueChanges.pipe(
            takeUntil(this.addDestroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (inputValue.length < 1) {
                    this.filteredTipuriProbleme = this.tipuriProbleme;
                    return of(this.tipuriProbleme);
                }
                return this.filterTipuriProbleme(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredTipuriProbleme = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterModule(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.module.filter(modul =>
            this.normalizeString(modul.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onModulSelectClosed() {
        const selectedValue = this.moduleControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredModule.some(modul =>
                modul.id === this.form.get('modul_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.moduleControl.setValue(null);
                this.filteredModule = this.module;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterLocatii(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.locatii.filter(locatie =>
            this.normalizeString(locatie.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onLocatieSelectClosed() {
        const selectedValue = this.locatiiControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredLocatii.some(locatie =>
                locatie.id === this.form.get('locatie_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.locatiiControl.setValue(null);
                this.filteredLocatii = this.locatii;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterSpecializari(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.specializari.filter(specializare =>
            this.normalizeString(specializare.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onSpecializareSelectClosed() {
        const selectedValue = this.specializariControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredSpecializari.some(specializare =>
                specializare.id === this.form.get('specializare_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.specializariControl.setValue(null);
                this.filteredSpecializari = this.specializari;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    filterOperatori(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.operatori.filter(operator =>
            this.normalizeString(operator.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onOperatorChange(specialist_id: any): void {
        if (specialist_id === -1 || specialist_id === '-1') {
            this.form.controls['nume_specialist'].patchValue('');
            return;
        }

        const selectedOperator = this.operatori.find(operator => operator.id_user === specialist_id);

        if (selectedOperator) {
            this.form.controls['nume_specialist'].patchValue(selectedOperator.name);
        }
    }

    onOperatoriSelectClosed() {
        const selectedValue = this.operatoriControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredOperatori.some(operator =>
                operator.id_user === this.form.get('specialist_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.operatoriControl.setValue(null);
                this.filteredOperatori = this.operatori;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    loadTipuriProbleme() {
        this.isLoading = true;

        this.tipuriProblemeService.listActive().pipe(
            map((results: any) => [...results.data]),
            finalize(() => {
                this.isLoading = false;
            })
        ).subscribe({
            next: (results) => {
                this.tipuriProbleme = results;
                this.filteredTipuriProbleme = results;
            },
            error: (err) => {
                console.error('Error in get tipuri probleme active:', err);
            }
        });
    }

    filterTipuriProbleme(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.tipuriProbleme.filter(tipProblema =>
            this.normalizeString(tipProblema.name).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onTipProblemaSelectClosed() {
        const selectedValue = this.tipuriProblemeControl.value;

        if (selectedValue) {
            const isSelectedInFilteredList = this.filteredTipuriProbleme.some(tipProblema =>
                tipProblema.id === this.form.get('tip_problema_id')?.value
            );

            if (!isSelectedInFilteredList) {
                this.tipuriProblemeControl.setValue(null);
                this.filteredTipuriProbleme = this.tipuriProbleme;
                this.addDestroy$.next();
                this.addDestroy$.complete();
                this.renderFieldsSearch();
            }
        }
    }

    submitForm() {
        this.isLoading = true;

        const { modul_id, locatie_id, specializare_id, specialist_id, nume_specialist, tip_problema_id } = this.form.value as ProgramUpdate;

        const updatedProgram: ProgramUpdate = {
            modul_id,
            locatie_id,
            specializare_id,
            specialist_id,
            nume_specialist,
            tip_problema_id,
            id: this.data.data.id,
        };

        this.programService
            .update(updatedProgram)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: () => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
